import { useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { KeyRound, Eye, EyeOff, Copy, RefreshCw, Check, ShieldCheck, Info, Trash2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Settings() {
  const [apiKey, setApiKey] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);

  const fetchKey = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ops/settings/api-key");
      setApiKey(data.key);
      setRevealed(true);
    } catch (e) {
      toast.error("Failed to fetch API key");
    }
    setLoading(false);
  }, []);

  const rotateKey = async () => {
    setRotating(true);
    try {
      const { data } = await api.post("/ops/settings/rotate-key");
      setApiKey(data.key);
      setRevealed(true);
      toast.success("API key rotated successfully. Update your MentorAI connector.");
    } catch (e) {
      toast.error("Failed to rotate API key");
    }
    setRotating(false);
  };

  const copyKey = () => {
    if (!apiKey) return;
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    toast.success("API key copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const maskKey = (key) => {
    if (!key) return "";
    return key.slice(0, 8) + "\u2022".repeat(24) + key.slice(-8);
  };

  const hideKey = () => {
    setRevealed(false);
    setApiKey(null);
  };

  const purgeData = async () => {
    setPurging(true);
    setPurgeResult(null);
    try {
      const { data } = await api.post("/ops/settings/purge-data");
      setPurgeResult(data.deleted);
      toast.success("All demo data purged. Dashboard is clean and ready for live connection.");
    } catch (e) {
      toast.error("Failed to purge data");
    }
    setPurging(false);
  };

  return (
    <div data-testid="ops-settings-page" className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-lg font-display font-semibold text-[hsl(var(--ops-text))]">Settings</h1>
        <p className="text-xs text-[hsl(var(--ops-text-muted))]">API key management and integration configuration</p>
      </div>

      {/* API Key Management */}
      <Card className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] overflow-hidden">
        <div className="px-5 py-4 border-b border-[hsl(var(--ops-border))]">
          <div className="flex items-center gap-2.5">
            <div className="size-8 rounded-lg bg-[hsl(var(--ops-brand))]/10 flex items-center justify-center">
              <KeyRound className="size-4 text-[hsl(var(--ops-brand))]" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-[hsl(var(--ops-text))]">Ingestion API Key</h2>
              <p className="text-[11px] text-[hsl(var(--ops-text-muted))]">Used by MentorAI to push telemetry to this dashboard</p>
            </div>
            <Badge className="ml-auto rounded text-[10px] border-0 bg-[hsl(var(--ops-long))]/10 text-[hsl(var(--ops-long))]">
              <ShieldCheck className="size-3 mr-1" /> Secured
            </Badge>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Key display */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-[hsl(var(--ops-text-muted))]">Current API Key</label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 max-sm:min-w-0">
                <Input
                  data-testid="ops-settings-api-key-display"
                  readOnly
                  value={revealed && apiKey ? apiKey : (apiKey ? maskKey(apiKey) : "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022  Click 'Reveal' to view")}
                  className="h-9 text-xs font-code bg-[hsl(var(--ops-surface-2))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))] pr-20"
                />
              </div>
              {!revealed ? (
                <Button
                  data-testid="ops-settings-reveal-key-btn"
                  variant="ghost"
                  size="sm"
                  className="h-9 px-3 text-xs gap-1.5 text-[hsl(var(--ops-text-muted))] hover:text-[hsl(var(--ops-text))] border border-[hsl(var(--ops-border))]"
                  onClick={fetchKey}
                  disabled={loading}
                >
                  {loading ? <RefreshCw className="size-3 animate-spin" /> : <Eye className="size-3.5" />}
                  Reveal
                </Button>
              ) : (
                <>
                  <Button
                    data-testid="ops-settings-copy-key-btn"
                    variant="ghost"
                    size="sm"
                    className="h-9 px-3 text-xs gap-1.5 text-[hsl(var(--ops-text-muted))] hover:text-[hsl(var(--ops-text))] border border-[hsl(var(--ops-border))]"
                    onClick={copyKey}
                  >
                    {copied ? <Check className="size-3.5 text-[hsl(var(--ops-long))]" /> : <Copy className="size-3.5" />}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                  <Button
                    data-testid="ops-settings-hide-key-btn"
                    variant="ghost"
                    size="sm"
                    className="h-9 px-3 text-xs gap-1.5 text-[hsl(var(--ops-text-muted))] hover:text-[hsl(var(--ops-text))] border border-[hsl(var(--ops-border))]"
                    onClick={hideKey}
                  >
                    <EyeOff className="size-3.5" />
                    Hide
                  </Button>
                </>
              )}
            </div>
          </div>

          <Separator className="bg-[hsl(var(--ops-border))]" />

          {/* Rotate Key */}
          <div className="flex items-center justify-between max-sm:gap-3">
            <div>
              <h3 className="text-xs font-medium text-[hsl(var(--ops-text))]">Rotate API Key</h3>
              <p className="text-[11px] text-[hsl(var(--ops-text-muted))] mt-0.5">
                Generate a new key. The old key will be immediately invalidated.
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  data-testid="ops-settings-rotate-key-btn"
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 text-xs gap-1.5 border-[hsl(var(--ops-warn))]/30 text-[hsl(var(--ops-warn))] hover:bg-[hsl(var(--ops-warn))]/10 hover:text-[hsl(var(--ops-warn))]"
                  disabled={rotating}
                >
                  {rotating ? <RefreshCw className="size-3 animate-spin" /> : <RefreshCw className="size-3" />}
                  Rotate Key
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))] max-sm:w-[calc(100%-1.5rem)] max-sm:rounded-lg max-sm:p-5 max-sm:max-h-[90dvh] max-sm:overflow-y-auto">
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <RefreshCw className="size-5 text-[hsl(var(--ops-warn))]" /> Rotate API Key
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-[hsl(var(--ops-text-muted))]">
                    This will generate a new API key and <strong>immediately invalidate the current one</strong>.
                    Your MentorAI connector will stop authenticating until you update it with the new key.
                    <br /><br />
                    Are you sure you want to proceed?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-[hsl(var(--ops-surface-2))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))] hover:bg-[hsl(var(--ops-surface))]">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="ops-settings-rotate-confirm-btn"
                    onClick={rotateKey}
                    className="bg-[hsl(var(--ops-warn))] text-white hover:bg-[hsl(var(--ops-warn))]/90"
                  >
                    <RefreshCw className="size-4 mr-1" /> Confirm Rotation
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </Card>

      {/* Usage Instructions */}
      <Card className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] overflow-hidden">
        <div className="px-5 py-4 border-b border-[hsl(var(--ops-border))]">
          <div className="flex items-center gap-2.5">
            <div className="size-8 rounded-lg bg-[hsl(var(--ops-brand))]/10 flex items-center justify-center">
              <Info className="size-4 text-[hsl(var(--ops-brand))]" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-[hsl(var(--ops-text))]">Integration Quick Reference</h2>
              <p className="text-[11px] text-[hsl(var(--ops-text-muted))]">How to use the API key in your MentorAI connector</p>
            </div>
          </div>
        </div>
        <div className="p-5 space-y-3">
          <div className="rounded-lg bg-[hsl(var(--ops-surface-2))] p-4 font-code text-[11px] text-[hsl(var(--ops-text-muted))] overflow-x-auto max-sm:[overflow-wrap:anywhere] space-y-1 leading-relaxed">
            <div className="text-[hsl(var(--ops-text))]"># Environment variable (recommended)</div>
            <div>DASHBOARD_API_KEY=<span className="text-[hsl(var(--ops-brand))]">your-api-key-here</span></div>
            <div className="mt-3 text-[hsl(var(--ops-text))]"># HTTP Header</div>
            <div>X-API-Key: <span className="text-[hsl(var(--ops-brand))]">your-api-key-here</span></div>
            <div className="mt-3 text-[hsl(var(--ops-text))]"># Test connectivity</div>
            <div>curl {window.location.origin}/api/ops/ping</div>
          </div>
          <div className="flex items-start gap-2 text-[11px] text-[hsl(var(--ops-text-muted))]">
            <ShieldCheck className="size-3.5 shrink-0 mt-0.5 text-[hsl(var(--ops-long))]" />
            <span>Store the key as an environment variable. Never hard-code it in source code or commit it to version control.</span>
          </div>
        </div>
      </Card>

      {/* Purge Data — Danger Zone */}
      <Card className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-critical))]/20 overflow-hidden">
        <div className="px-5 py-4 border-b border-[hsl(var(--ops-critical))]/20">
          <div className="flex items-center gap-2.5">
            <div className="size-8 rounded-lg bg-[hsl(var(--ops-critical))]/10 flex items-center justify-center">
              <Trash2 className="size-4 text-[hsl(var(--ops-critical))]" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-[hsl(var(--ops-text))]">Purge All Data</h2>
              <p className="text-[11px] text-[hsl(var(--ops-text-muted))]">Remove all demo/simulated data before connecting the real MentorAI system</p>
            </div>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-start gap-2 text-[11px] text-[hsl(var(--ops-text-muted))] bg-[hsl(var(--ops-critical))]/5 rounded-lg p-3">
            <AlertTriangle className="size-3.5 shrink-0 mt-0.5 text-[hsl(var(--ops-critical))]" />
            <div>
              <span className="font-medium text-[hsl(var(--ops-text))]">This will permanently delete:</span>
              <span> All candles, trades, signals, logs, positions, orders, accounts, and alerts. The status will be reset to a clean baseline. Your API key will be preserved.</span>
            </div>
          </div>

          <div className="flex items-center justify-between max-sm:gap-3">
            <div>
              <h3 className="text-xs font-medium text-[hsl(var(--ops-text))]">Clean Slate for Production</h3>
              <p className="text-[11px] text-[hsl(var(--ops-text-muted))] mt-0.5">
                Run this once before your first live MentorAI connection.
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  data-testid="ops-settings-purge-btn"
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 text-xs gap-1.5 border-[hsl(var(--ops-critical))]/30 text-[hsl(var(--ops-critical))] hover:bg-[hsl(var(--ops-critical))]/10 hover:text-[hsl(var(--ops-critical))]"
                  disabled={purging}
                >
                  {purging ? <RefreshCw className="size-3 animate-spin" /> : <Trash2 className="size-3" />}
                  Purge All Data
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))] max-sm:w-[calc(100%-1.5rem)] max-sm:rounded-lg max-sm:p-5 max-sm:max-h-[90dvh] max-sm:overflow-y-auto">
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="size-5 text-[hsl(var(--ops-critical))]" /> Purge All Dashboard Data
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-[hsl(var(--ops-text-muted))]">
                    This will <strong>permanently delete</strong> all candles, trades, signals, logs, positions, orders, accounts, and alerts from the dashboard.
                    <br /><br />
                    The bot status will be reset. Your API key will be preserved.
                    <br /><br />
                    <strong>This cannot be undone.</strong> Only do this if you're ready to connect the real MentorAI system.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-[hsl(var(--ops-surface-2))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))] hover:bg-[hsl(var(--ops-surface))]">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="ops-settings-purge-confirm-btn"
                    onClick={purgeData}
                    className="bg-[hsl(var(--ops-critical))] text-white hover:bg-[hsl(var(--ops-critical))]/90"
                  >
                    <Trash2 className="size-4 mr-1" /> Yes, Purge Everything
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          {purgeResult && (
            <div className="rounded-lg bg-[hsl(var(--ops-surface-2))] p-3 text-[11px] font-code text-[hsl(var(--ops-text-muted))]">
              <div className="text-[hsl(var(--ops-long))] font-medium mb-1">Purge complete:</div>
              {Object.entries(purgeResult).map(([coll, count]) => (
                <div key={coll}>{coll}: <span className="text-[hsl(var(--ops-text))]">{count} deleted</span></div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
